/**
 * Keyboard movement across the occupancy grid.
 *
 * The grid is one tab stop and the arrow keys move within it — a roving
 * tabindex. Making every cell tabbable instead put roughly fifteen hundred stops
 * between the grid and anything below it on a twenty-nine boat fleet, so
 * reaching the footer meant holding Tab through a year of weeks.
 *
 * Kept as a pure function so the movement rules are tested without a DOM, which
 * is all the web app's test environment offers.
 */

export interface GridPosition {
  row: number;
  col: number;
}

export interface GridBounds {
  rows: number;
  cols: number;
}

const clamp = (v: number, max: number) => Math.min(max, Math.max(0, v));

/** The next position, or null when the key is not a movement key. */
export function moveGridFocus(
  current: GridPosition,
  key: string,
  bounds: GridBounds
): GridPosition | null {
  if (bounds.rows <= 0 || bounds.cols <= 0) return null;

  // A filter can shrink the fleet while a cell is focused, so the current
  // position is clamped before moving rather than trusted.
  const row = clamp(current.row, bounds.rows - 1);
  const col = clamp(current.col, bounds.cols - 1);

  switch (key) {
    // Edges clamp rather than wrap: the right edge of December is not next to
    // January, and wrapping a 53-week row jumps a year without warning.
    case 'ArrowRight':
      return { row, col: clamp(col + 1, bounds.cols - 1) };
    case 'ArrowLeft':
      return { row, col: clamp(col - 1, bounds.cols - 1) };
    case 'ArrowDown':
      return { row: clamp(row + 1, bounds.rows - 1), col };
    case 'ArrowUp':
      return { row: clamp(row - 1, bounds.rows - 1), col };
    case 'Home':
      return { row, col: 0 };
    case 'End':
      return { row, col: bounds.cols - 1 };
    default:
      return null;
  }
}
